import React from 'react';

import type { RawEditAnalyticsEvent } from '@gitbook/app-analytics';
import {
    BLOCK_CODE,
    BLOCK_COLUMNS,
    BLOCK_EXPANDABLE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HINT,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_STEPPER,
    BLOCK_TABS,
    canInsertBlockIn,
    expandable,
    LIST_BLOCKS,
    tabs,
} from '@gitbook/doc-core';
import { createCodeBlock } from '@gitbook/doc-core/src/code/utils';
import { createColumnsBlock } from '@gitbook/doc-core/src/columns';
import { createHint } from '@gitbook/doc-core/src/hint/utils';
import { createList } from '@gitbook/doc-core/src/list';
import { createParagraph } from '@gitbook/doc-core/src/paragraph';
import { createStepper } from '@gitbook/doc-core/src/stepper';
import {
    createPalette,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteGroupDef,
    PalettePositionMaxHeight,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import {
    type AncestorNode,
    Block,
    BlockRange,
    type Change,
    generateKey,
    Inline,
} from '@gitbook/slate';
import { type EditorContainer, useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertNever, filterOutNullable } from '@gitbook/util-ts';

import { ContentEditorContext } from '../contexts';
import { useReusableContentPaletteGroup } from '../reusable-content';
import { useBlockPaletteFactory } from './blockPalette';

/**
 * Hook to use a commands palette definition to edit a text block (paragraph, headings, etc).
 */
export function useTextBlockPaletteFactory(
    nodeKey: string,
    options?: {
        filter?: BlockType[];
        extraGroups?: PaletteGroupDef[];
        aiCompletion?: boolean;
        asGrid?: boolean;
    }
) {
    const editor = useEditor();
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const createReusableContentGroup = useReusableContentPaletteGroup({
        nodeKey,
        method: 'editor-palette',
        title: 'Reusable content',
    });

    const createExtraGroups = useEventCallback(() => {
        const extraGroups = options?.extraGroups || [];
        const groups: PaletteGroupDef[] = [];
        const reusableContentGroup = createReusableContentGroup();
        const reusableContentCommands = reusableContentGroup?.commands || [];

        // The text block palette is rendered as a grid of icon-only items in the format range toolbar
        if (options?.asGrid) {
            const block = editor.value.document.assertBlock(nodeKey);
            const parent = editor.value.document.getParent(nodeKey);

            groups.push({
                key: 'commands_text',
                title: 'Text',
                commands: () => {
                    if (!parent) {
                        return [];
                    }

                    return [
                        getBlockCommand(editor, block, BLOCK_PARAGRAPH, logAnalyticsEvent),
                        canInsertBlockIn(parent, BLOCK_HEADING_1)
                            ? getBlockCommand(editor, block, BLOCK_HEADING_1, logAnalyticsEvent)
                            : null,
                        canInsertBlockIn(parent, BLOCK_HEADING_2)
                            ? getBlockCommand(editor, block, BLOCK_HEADING_2, logAnalyticsEvent)
                            : null,
                        canInsertBlockIn(parent, BLOCK_HEADING_3)
                            ? getBlockCommand(editor, block, BLOCK_HEADING_3, logAnalyticsEvent)
                            : null,
                    ].filter(filterOutNullable);
                },
            });

            groups.push({
                key: 'commands_lists',
                title: 'Lists',
                commands: () => {
                    const listBlock = parent ? editor.value.document.getParent(parent.key) : null;

                    if (!parent) {
                        return [];
                    }

                    return [
                        canInsertBlockIn(parent, BLOCK_LIST_UL)
                            ? getBlockCommand(
                                  editor,
                                  block,
                                  BLOCK_LIST_UL,
                                  logAnalyticsEvent,
                                  listBlock
                              )
                            : null,
                        canInsertBlockIn(parent, BLOCK_LIST_OL)
                            ? getBlockCommand(
                                  editor,
                                  block,
                                  BLOCK_LIST_OL,
                                  logAnalyticsEvent,
                                  listBlock
                              )
                            : null,
                        canInsertBlockIn(parent, BLOCK_LIST_TASKS)
                            ? getBlockCommand(
                                  editor,
                                  block,
                                  BLOCK_LIST_TASKS,
                                  logAnalyticsEvent,
                                  listBlock
                              )
                            : null,
                    ].filter(filterOutNullable);
                },
            });

            groups.push({
                key: 'commands_other',
                title: 'Other',
                commands: () => {
                    if (!parent) {
                        return [];
                    }

                    return [
                        canInsertBlockIn(parent, BLOCK_HINT)
                            ? getBlockCommand(editor, block, BLOCK_HINT, logAnalyticsEvent)
                            : null,
                        canInsertBlockIn(parent, BLOCK_CODE)
                            ? getBlockCommand(editor, block, BLOCK_CODE, logAnalyticsEvent)
                            : null,
                        canInsertBlockIn(parent, BLOCK_QUOTE)
                            ? getBlockCommand(editor, block, BLOCK_QUOTE, logAnalyticsEvent, parent)
                            : null,
                        canInsertBlockIn(parent, BLOCK_TABS)
                            ? getBlockCommand(editor, block, BLOCK_TABS, logAnalyticsEvent)
                            : null,
                        canInsertBlockIn(parent, BLOCK_STEPPER)
                            ? getBlockCommand(editor, block, BLOCK_STEPPER, logAnalyticsEvent)
                            : null,
                        canInsertBlockIn(parent, BLOCK_EXPANDABLE)
                            ? getBlockCommand(editor, block, BLOCK_EXPANDABLE, logAnalyticsEvent)
                            : null,
                        canInsertBlockIn(parent, BLOCK_COLUMNS)
                            ? getBlockCommand(editor, block, BLOCK_COLUMNS, logAnalyticsEvent)
                            : null,
                    ].filter(filterOutNullable);
                },
            });
        } else {
            groups.push({
                key: 'turn_into',
                title: 'Turn into',
                commands: () => {
                    const block = editor.value.document.assertBlock(nodeKey);
                    const parent = editor.value.document.getParent(nodeKey);

                    if (!parent) {
                        return [];
                    }

                    return [
                        getBlockCommand(editor, block, BLOCK_PARAGRAPH, logAnalyticsEvent),
                        canInsertBlockIn(parent, BLOCK_HEADING_1)
                            ? getBlockCommand(editor, block, BLOCK_HEADING_1, logAnalyticsEvent)
                            : null,
                        canInsertBlockIn(parent, BLOCK_HEADING_2)
                            ? getBlockCommand(editor, block, BLOCK_HEADING_2, logAnalyticsEvent)
                            : null,
                        canInsertBlockIn(parent, BLOCK_HEADING_3)
                            ? getBlockCommand(editor, block, BLOCK_HEADING_3, logAnalyticsEvent)
                            : null,
                        {
                            key: 'turn_into_more',
                            title: 'More blocks',
                            icon: Icon.Replace,
                            descendant: () => {
                                return createPalette({
                                    search: { placeholder: 'Search blocks...' },
                                    position: {
                                        width: PalettePositionWidth.ANCHOR,
                                    },
                                    groups: [
                                        reusableContentCommands.length > 0
                                            ? {
                                                  key: 'reusable_content',
                                                  commands: reusableContentCommands,
                                              }
                                            : null,
                                        {
                                            key: 'block_types',
                                            title: 'Blocks',
                                            commands: [
                                                canInsertBlockIn(parent, BLOCK_HINT)
                                                    ? getBlockCommand(
                                                          editor,
                                                          block,
                                                          BLOCK_HINT,
                                                          logAnalyticsEvent
                                                      )
                                                    : null,
                                                canInsertBlockIn(parent, BLOCK_LIST_UL)
                                                    ? getBlockCommand(
                                                          editor,
                                                          block,
                                                          BLOCK_LIST_UL,
                                                          logAnalyticsEvent
                                                      )
                                                    : null,
                                                canInsertBlockIn(parent, BLOCK_LIST_OL)
                                                    ? getBlockCommand(
                                                          editor,
                                                          block,
                                                          BLOCK_LIST_OL,
                                                          logAnalyticsEvent
                                                      )
                                                    : null,
                                                canInsertBlockIn(parent, BLOCK_LIST_TASKS)
                                                    ? getBlockCommand(
                                                          editor,
                                                          block,
                                                          BLOCK_LIST_TASKS,
                                                          logAnalyticsEvent
                                                      )
                                                    : null,
                                                canInsertBlockIn(parent, BLOCK_CODE)
                                                    ? getBlockCommand(
                                                          editor,
                                                          block,
                                                          BLOCK_CODE,
                                                          logAnalyticsEvent
                                                      )
                                                    : null,
                                                canInsertBlockIn(parent, BLOCK_QUOTE)
                                                    ? getBlockCommand(
                                                          editor,
                                                          block,
                                                          BLOCK_QUOTE,
                                                          logAnalyticsEvent
                                                      )
                                                    : null,
                                                canInsertBlockIn(parent, BLOCK_TABS)
                                                    ? getBlockCommand(
                                                          editor,
                                                          block,
                                                          BLOCK_TABS,
                                                          logAnalyticsEvent
                                                      )
                                                    : null,
                                                canInsertBlockIn(parent, BLOCK_STEPPER)
                                                    ? getBlockCommand(
                                                          editor,
                                                          block,
                                                          BLOCK_STEPPER,
                                                          logAnalyticsEvent
                                                      )
                                                    : null,
                                                canInsertBlockIn(parent, BLOCK_EXPANDABLE)
                                                    ? getBlockCommand(
                                                          editor,
                                                          block,
                                                          BLOCK_EXPANDABLE,
                                                          logAnalyticsEvent
                                                      )
                                                    : null,
                                                canInsertBlockIn(parent, BLOCK_COLUMNS)
                                                    ? getBlockCommand(
                                                          editor,
                                                          block,
                                                          BLOCK_COLUMNS,
                                                          logAnalyticsEvent
                                                      )
                                                    : null,
                                            ].filter(filterOutNullable),
                                        },
                                    ].filter(filterOutNullable),
                                });
                            },
                        },
                    ]
                        .filter(filterOutNullable)
                        .filter((command) => !options?.filter?.includes(command.key));
                },
            });

            groups.push({
                key: 'align',
                commands: () => {
                    const block = editor.value.document.assertBlock(nodeKey);
                    const align = block.data.get('align');
                    const changeAlign = (value: 'start' | 'center' | 'end') => {
                        editor.change((c) => {
                            c.insertDataByKey(block.key, ['align'], value);
                        });
                    };
                    return [
                        {
                            key: 'start',
                            type: PaletteCommandType.RADIO,
                            title: 'Left align',
                            selected: align === 'start' || typeof align === 'undefined',
                            icon: Icon.AlignTextLeft,
                            trigger: () => changeAlign('start'),
                        } satisfies PaletteCommandDef,
                        {
                            key: 'center',
                            type: PaletteCommandType.RADIO,
                            title: 'Center',
                            selected: align === 'center',
                            icon: Icon.AlignTextCenter,
                            trigger: () => changeAlign('center'),
                        } satisfies PaletteCommandDef,
                        {
                            key: 'end',
                            type: PaletteCommandType.RADIO,
                            title: 'Right align',
                            selected: align === 'end',
                            icon: Icon.AlignTextRight,
                            trigger: () => changeAlign('end'),
                        } satisfies PaletteCommandDef,
                    ];
                },
            });
        }

        groups.push(...extraGroups);

        return groups;
    });

    return useBlockPaletteFactory({
        nodeKey,
        aiCompletion: options?.aiCompletion ?? true,
        addReusableContentGroup: false, // Disable top-level reusable content group
        addConditionalContentGroup: false,
        createExtraGroups,
        asGrid: options?.asGrid,
        position: {
            maxHeight: options?.asGrid ? undefined : PalettePositionMaxHeight.AUTO,
            width: options?.asGrid ? PalettePositionWidth.ELEMENT : undefined,
        },
    });
}

export type BlockType =
    | typeof BLOCK_PARAGRAPH
    | typeof BLOCK_HEADING_1
    | typeof BLOCK_HEADING_2
    | typeof BLOCK_HEADING_3
    | typeof BLOCK_HINT
    | typeof BLOCK_QUOTE
    | typeof BLOCK_LIST_UL
    | typeof BLOCK_LIST_OL
    | typeof BLOCK_LIST_TASKS
    | typeof BLOCK_CODE
    | typeof BLOCK_EXPANDABLE
    | typeof BLOCK_STEPPER
    | typeof BLOCK_TABS
    | typeof BLOCK_COLUMNS;

export const BlocksMap: Record<
    BlockType,
    {
        label: string;
        icon: (typeof Icon)[keyof typeof Icon];
        type: BlockType;
    }
> = {
    [BLOCK_PARAGRAPH]: {
        label: 'Paragraph',
        icon: Icon.Paragraph,
        type: BLOCK_PARAGRAPH,
    },
    [BLOCK_HEADING_1]: {
        label: 'Heading 1',
        icon: Icon.Heading1,
        type: BLOCK_HEADING_1,
    },
    [BLOCK_HEADING_2]: {
        label: 'Heading 2',
        icon: Icon.Heading2,
        type: BLOCK_HEADING_2,
    },
    [BLOCK_HEADING_3]: {
        label: 'Heading 3',
        icon: Icon.Heading3,
        type: BLOCK_HEADING_3,
    },
    [BLOCK_HINT]: {
        label: 'Hint',
        icon: Icon.InfoCircle,
        type: BLOCK_HINT,
    },
    [BLOCK_QUOTE]: {
        label: 'Quote',
        icon: Icon.Quote,
        type: BLOCK_QUOTE,
    },
    [BLOCK_LIST_UL]: {
        label: 'Unordered List',
        icon: Icon.ListUnordered,
        type: BLOCK_LIST_UL,
    },
    [BLOCK_LIST_OL]: {
        label: 'Ordered List',
        icon: Icon.ListOrdered,
        type: BLOCK_LIST_OL,
    },
    [BLOCK_LIST_TASKS]: {
        label: 'Task List',
        icon: Icon.ListTasks,
        type: BLOCK_LIST_TASKS,
    },
    [BLOCK_CODE]: {
        label: 'Code block',
        icon: Icon.Code,
        type: BLOCK_CODE,
    },
    [BLOCK_EXPANDABLE]: {
        label: 'Expandable',
        icon: Icon.BlockExpandable,
        type: BLOCK_EXPANDABLE,
    },
    [BLOCK_STEPPER]: {
        label: 'Stepper',
        icon: Icon.Stepper,
        type: BLOCK_STEPPER,
    },
    [BLOCK_TABS]: {
        label: 'Tabs',
        icon: Icon.Tabs,
        type: BLOCK_TABS,
    },
    [BLOCK_COLUMNS]: {
        label: 'Columns',
        icon: Icon.Columns,
        type: BLOCK_COLUMNS,
    },
};

/**
 * Clone the nodes of the paragraph so we preserve any marks and rich formatting instead of just passing the raw text.
 */
const cloneParagraphNodes = (p: Block | AncestorNode) =>
    p.nodes.toArray().map((n) => n.merge({ key: generateKey() }));

/**
 * Convert a paragraph → any other block type that the palette supports.
 * Decides between:
 *   - setNodeByKey (simply switch the block type, for blocks that share structure with paragraphs)
 *   – wrapBlockByKey (for simple containers: hint, quote etc)
 *   – replaceNodeByKey (for complex structures: lists, tabs, expandable etc)
 */
export function paragraphToBlock(
    change: Change,
    paragraph: Block | AncestorNode,
    target: BlockType
) {
    const keep = cloneParagraphNodes(paragraph);

    switch (target) {
        case BLOCK_PARAGRAPH:
        case BLOCK_HEADING_1:
        case BLOCK_HEADING_2:
        case BLOCK_HEADING_3: {
            change.setNodeByKey(paragraph.key, { type: target });
            change.selectBlock(
                BlockRange.create({
                    anchorBlock: paragraph.key,
                    focusBlock: paragraph.key,
                    isFocused: true,
                })
            );
            break;
        }

        case BLOCK_HINT: {
            change.wrapBlockByKey(paragraph.key, {
                type: BLOCK_HINT,
                data: createHint().data,
            });
            change.selectBlock(
                BlockRange.create({
                    anchorBlock: paragraph.key,
                    focusBlock: paragraph.key,
                    isFocused: true,
                })
            );
            break;
        }

        case BLOCK_LIST_UL:
        case BLOCK_LIST_OL:
        case BLOCK_LIST_TASKS: {
            const list = createList(target, keep);
            change.replaceNodeByKey(paragraph.key, list);
            change.selectBlock(
                BlockRange.create({
                    anchorBlock: list.key,
                    focusBlock: list.key,
                    isFocused: true,
                })
            );
            break;
        }

        case BLOCK_CODE: {
            const code = createCodeBlock(keep);
            change.replaceNodeByKey(paragraph.key, code);
            change.selectBlock(
                BlockRange.create({
                    anchorBlock: code.key,
                    focusBlock: code.key,
                    isFocused: true,
                })
            );
            break;
        }

        case BLOCK_QUOTE: {
            change.wrapBlockByKey(paragraph.key, { type: BLOCK_QUOTE });
            change.selectBlock(
                BlockRange.create({
                    anchorBlock: paragraph.key,
                    focusBlock: paragraph.key,
                    isFocused: true,
                })
            );
            break;
        }

        case BLOCK_TABS: {
            const body = Block.create({
                type: BLOCK_PARAGRAPH,
                nodes: keep,
            });
            const tabsBlock = tabs.createTabs([
                tabs.createTabsItem('Tab 1', [body]),
                tabs.createTabsItem('Tab 2'),
            ]);
            change.replaceNodeByKey(paragraph.key, tabsBlock);
            change.selectBlock(
                BlockRange.create({
                    anchorBlock: tabsBlock.key,
                    focusBlock: tabsBlock.key,
                    isFocused: true,
                })
            );
            break;
        }

        case BLOCK_STEPPER: {
            const inline = Inline.create({ nodes: cloneParagraphNodes(paragraph) });
            const stepper = createStepper([inline]);
            change.replaceNodeByKey(paragraph.key, stepper);
            change.selectBlock(
                BlockRange.create({
                    anchorBlock: stepper.key,
                    focusBlock: stepper.key,
                    isFocused: true,
                })
            );
            break;
        }

        case BLOCK_EXPANDABLE: {
            const exp = expandable.createExpandableBlock(keep);
            change.replaceNodeByKey(paragraph.key, exp);
            change.selectBlock(
                BlockRange.create({
                    anchorBlock: exp.key,
                    focusBlock: exp.key,
                    isFocused: true,
                })
            );
            break;
        }

        case BLOCK_COLUMNS: {
            const columns = createColumnsBlock([[createParagraph(keep)], [createParagraph()]]);
            change.replaceNodeByKey(paragraph.key, columns);
            change.selectBlock(
                BlockRange.create({
                    anchorBlock: columns.key,
                    focusBlock: columns.key,
                    isFocused: true,
                })
            );
            break;
        }

        default:
            assertNever(target);
    }
}

/**
 * Return the palette command for a given block type.
 */
function getBlockCommand(
    editor: EditorContainer,
    block: Block,
    blockType: BlockType,
    logAnalyticsEvent: (event: RawEditAnalyticsEvent) => void,
    parent?: AncestorNode | null
): PaletteCommandDef {
    return {
        key: blockType,
        title: BlocksMap[blockType].label,
        type: PaletteCommandType.RADIO,
        selected: block.type === blockType || parent?.type === blockType,
        icon: BlocksMap[blockType].icon,
        trigger: () => {
            editor.change((change) => {
                logAnalyticsEvent({
                    type: 'edit_page_document_edit_block_type',
                    fromType: block.type,
                    toType: blockType,
                });

                if (parent && LIST_BLOCKS.includes(parent.type ?? '')) {
                    paragraphToBlock(change, parent, blockType);
                } else {
                    paragraphToBlock(change, block, blockType);
                }
            });
        },
    };
}
